import React, { createContext, useContext } from 'react';
import { notification } from 'antd';
import { playSuccessTone, playErrorTone } from '../utils/sound';

const NotificationContext = createContext<{
    contextHolder: React.ReactElement;
    showSuccess: (content: string) => void;
    showError: (content: string) => void;
    showWarning: (content: string) => void;
    showInfo: (content: string) => void;
} | null>(null);

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [notificationApi, contextHolder] = notification.useNotification();

    const showSuccess = (content: string) => {
        playSuccessTone();
        notificationApi.success({
            message: 'Success',
            description: content,
            placement: 'topRight',
            duration: 3
        });
    };

    const showError = (content: string) => {
        playErrorTone();
        notificationApi.error({
            message: 'Error',
            description: content,
            placement: 'topRight',
            duration: 4
        });
    };

    const showWarning = (content: string) => {
        notificationApi.warning({
            message: 'Warning',
            description: content,
            placement: 'topRight',
            duration: 3
        });
    };

    const showInfo = (content: string) => {
        notificationApi.info({
            message: 'Information',
            description: content,
            placement: 'topRight',
            duration: 3
        });
    };

    return (
        <NotificationContext.Provider value={{ contextHolder, showSuccess, showError, showWarning, showInfo }}>
            {contextHolder}
            {children}
        </NotificationContext.Provider>
    );
};

export const useNotification = () => {
    const context = useContext(NotificationContext);
    if (!context) {
        throw new Error('useNotification must be used within a NotificationProvider');
    }
    return context;
};
